// Tax & Finance Operations: page text and the list of countries. Generated file.
const config = {
  "slug": "tax-finance-operations",
  "name": "Tax & Finance Operations",
  "menu": {
    "show": false,
    "label": "Tax & Finance Operations",
    "desc": "Reconciliations, month-end checks and the global tax calendar"
  },
  "meta": {
    "title": "Tax & Finance Operations: Month-End Tax Checks | Taxera",
    "description": "Reconcile VAT returns, e-invoices, SAF-T and EC Sales Lists to your ledgers each month, with alerts and a global tax calendar, all from your ERP data."
  },
  "hero": {
    "h1": "Month-end tax checks, without the spreadsheet chase.",
    "h1Accent": "Everything cross-checked, in one place.",
    "text": "Tax and finance teams spend the end of every month proving that the numbers agree. Taxera checks your VAT returns, e-invoices, SAF-T files and EC Sales Lists against your ledgers from the same ERP data, flags the differences and keeps track of what is due.",
    "secondaryCta": "See what is included"
  },
  "countriesSection": {
    "heading": "Tax and finance operations by country",
    "intro": "Country-specific guidance sits on each product page.",
    "trackerPath": "/tracker"
  },
  "what": {
    "heading": "What are tax and finance operations, in plain words?",
    "paragraphs": [
      "Filing a tax return is only the end of the process. Before it, finance and tax teams have to show that the return, the invoices, the audit files and the ledgers all agree. In most companies that means exports, spreadsheets and chasing colleagues at every month-end.",
      "Authorities now receive the same data in several forms: e-invoices, SAF-T files, VAT returns and EC Sales Lists. They compare them. A difference that nobody noticed internally can become a question, or a penalty, later. Tax and finance operations is the work of finding those differences first."
    ],
    "explainer": {
      "heading": "Three questions every month-end has to answer",
      "items": [
        {
          "icon": "Scale",
          "title": "Do the numbers agree?",
          "text": "The VAT return, the invoice data and the ledgers should tell the same story. Where they do not, someone has to find out why."
        },
        {
          "icon": "Search",
          "title": "Can we explain every difference?",
          "text": "For each gap you need to go from the total to the document behind it, quickly, and show the answer to an auditor."
        },
        {
          "icon": "CalendarDays",
          "title": "What is due, and when?",
          "text": "Deadlines are spread across countries and obligations. A calendar and alerts keep nothing from being missed."
        }
      ]
    }
  },
  "apps": {
    "heading": "What is included",
    "intro": "Each check works on the same ERP data that Taxera already uses for your e-Invoicing, SAF-T and VAT reporting, so there is nothing extra to export.",
    "items": [
      {
        "icon": "Scale",
        "title": "Monthly Reconciliation",
        "text": "Compare your tax figures with the ledgers for the period, and see the differences in one view.",
        "bullets": [
          "Tax figures matched to ledger balances",
          "Differences flagged for review",
          "Drill-down from the total to the document"
        ]
      },
      {
        "icon": "FileCheck2",
        "title": "e-Invoicing Reconciliation",
        "text": "Check that the invoices sent through e-Invoicing match what is in your ledgers.",
        "bullets": [
          "Invoices in the ledger compared with invoices sent",
          "Missing or mismatched documents flagged",
          "Same data as your e-Invoicing setup"
        ]
      },
      {
        "icon": "Link2",
        "title": "VAT to EC Sales List",
        "text": "Check that the EC Sales List agrees with the intra-EU figures on the VAT return.",
        "bullets": [
          "EC Sales List compared with the VAT return",
          "Differences by customer and by period",
          "Fixed before either one is filed"
        ]
      },
      {
        "icon": "FileSpreadsheet",
        "title": "SAF-T Reconciliation",
        "text": "Check that the SAF-T audit file agrees with your ledgers before it goes to the authority.",
        "bullets": [
          "SAF-T totals compared with the ledgers",
          "Source data analytics to find the cause",
          "Same source as your SAF-T reporting"
        ]
      },
      {
        "icon": "BellRing",
        "title": "Notifications and Alerts",
        "text": "Get told when something needs attention, such as a difference, a deadline or a rule change.",
        "bullets": [
          "Alerts for the issues that need action",
          "Rule changes that affect your countries"
        ]
      },
      {
        "icon": "CalendarDays",
        "title": "Global Tax Calendar",
        "text": "See what is due, in which country, in one calendar.",
        "bullets": [
          "Deadlines across countries and obligations",
          "One view for the whole team"
        ]
      }
    ]
  },
  "steps": {
    "heading": "How it works",
    "intro": "The checks run on the data that is already flowing through Taxera.",
    "items": [
      {
        "icon": "PlugZap",
        "title": "Connect to your ERP",
        "text": "One integration serves e-Invoicing, SAF-T, VAT returns and these checks. No file exports and no manual uploads."
      },
      {
        "icon": "RefreshCw",
        "title": "Run the checks",
        "text": "Each month, Taxera compares the returns, invoices and audit files with your ledgers."
      },
      {
        "icon": "Search",
        "title": "Find the cause",
        "text": "Differences are listed with drill-down to the source documents, so the reason is visible."
      },
      {
        "icon": "CheckCircle2",
        "title": "Sign off with a trail",
        "text": "Your team reviews and signs off, and the evidence is there when an auditor or an authority asks."
      }
    ]
  },
  "benefits": {
    "heading": "What changes for your team",
    "items": [
      {
        "icon": "FileSpreadsheet",
        "title": "Less month-end chasing",
        "text": "The comparisons are done for you, so people spend time on the differences instead of building the comparison."
      },
      {
        "icon": "Layers",
        "title": "One platform, one source",
        "text": "The checks use the same data as your returns, invoices and audit files, so they cannot drift apart."
      },
      {
        "icon": "ShieldCheck",
        "title": "Problems found first",
        "text": "You see the mismatch before the authority does."
      },
      {
        "icon": "ListChecks",
        "title": "A clear audit trail",
        "text": "Every check can be traced to the documents behind it."
      }
    ]
  },
  "faq": {
    "heading": "Tax and finance operations, questions answered",
    "items": [
      {
        "q": "What is a tax reconciliation?",
        "a": "A tax reconciliation compares tax figures, such as a VAT return, with another source, such as the ledgers or the invoice data, and explains any difference. It shows that the numbers you report agree with your books."
      },
      {
        "q": "Why do tax and finance teams reconcile every month?",
        "a": "Authorities increasingly compare e-invoices, SAF-T files, VAT returns and EC Sales Lists. Reconciling each month finds differences while they are small and easy to explain, before a return is filed."
      },
      {
        "q": "What does Taxera reconcile?",
        "a": "Taxera checks your tax figures against the ledgers each month, compares e-invoices with the ledgers, compares the EC Sales List with the VAT return, and compares SAF-T files with the ledgers."
      },
      {
        "q": "Do we need to export data for these checks?",
        "a": "No. The checks run on the same ERP data that Taxera already uses for your e-Invoicing, SAF-T and VAT reporting."
      },
      {
        "q": "How does this help with audits?",
        "a": "Each difference can be traced from the total to the document behind it, and sign-off is recorded, so the evidence is ready when an auditor asks."
      },
      {
        "q": "Is this a separate product from VAT returns and e-Invoicing?",
        "a": "It is part of the same platform. The checks use the data from your returns, invoices and audit files, so there is one integration, not several."
      }
    ]
  },
  "related": [
    {
      "to": "/solutions/vat-returns",
      "label": "VAT Returns",
      "note": "The returns being checked"
    },
    {
      "to": "/solutions/e-invoicing",
      "label": "e-Invoicing",
      "note": "Invoices compared with the ledgers"
    },
    {
      "to": "/solutions/saf-t",
      "label": "SAF-T",
      "note": "Audit files checked before they go"
    },
    {
      "to": "/solutions/ec-sales-list",
      "label": "EC Sales List",
      "note": "Matched to the VAT return"
    }
  ],
  "cta": {
    "heading": "See month-end checks running on your own ERP data",
    "text": "Book a short demo. We will show you how differences are found and traced to the document."
  },
  "country": {
    "eyebrow": "Tax and finance operations {name}",
    "headingShort": "Tax and finance operations in {name}: {short}",
    "headingPlain": "Tax and finance operations in {name}",
    "mandateLabel": "The obligation",
    "whoLabel": "Who is affected",
    "howLabel": "How it works",
    "datesHeading": "Key dates for {name}",
    "leftHeading": "What is checked",
    "rightHeading": "What your ERP has to deliver",
    "flowHeading": "The process, step by step",
    "linkedHeading": "Linked obligations in {name}",
    "faqHeading": "Questions, answered",
    "moreHeading": "More guides",
    "allLabel": "All countries",
    "otherLabel": "{name}",
    "downloadTitle": "Keep this guide",
    "downloadText": "Download the {name} guide as a PDF.",
    "printTitle": "Guide: {name}",
    "trackerPath": "/tracker",
    "faqs": [
      {
        "q": "What is the obligation in {name}?",
        "a": [
          "mandateName",
          "summary"
        ]
      },
      {
        "q": "Who is affected in {name}?",
        "a": [
          "inScope"
        ]
      },
      {
        "q": "What are the key dates in {name}?",
        "a": [
          "keyDates"
        ]
      },
      {
        "q": "How does it work in {name}?",
        "a": [
          "process"
        ]
      },
      {
        "q": "What is checked in {name}?",
        "a": [
          "businessProcess"
        ]
      },
      {
        "q": "Where are the official rules for {name}?",
        "a": [
          "officialSources"
        ]
      }
    ]
  },
  "countries": []
};
export default config;
